import { useCallback, useEffect, useState } from "react";
import { http } from "../lib/api";
import { fmtTL, fmtNum } from "../lib/format";
import { Wallet, Users, LayoutDashboard, Clock, CheckCircle2, Building2, Landmark, FolderKanban, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import InstallmentCalendar from "../components/loans/InstallmentCalendar";
import WelcomeCard from "../components/dashboard/WelcomeCard";
import InsuranceDueCard from "../components/dashboard/InsuranceDueCard";
import BranchSummary from "../components/dashboard/BranchSummary";
import ProjectCards from "../components/dashboard/ProjectCards";
import PendingApprovalsPanel from "../components/dashboard/PendingApprovalsPanel";

const EMPTY = { view: "admin", totals: { entries: 0, customer: 0, subcontractor: 0, gross_profit: 0, pending: 0, approved: 0 }, projects: [], locations: null, cards: null, scope_names: [] };

// Görünüm bazlı KPI kartları: yönetici çalışan/ATM/şube/proje sayıları; proje koordinatörü kendi projelerinin mali özeti; şube/GM yetkilisi hizmet toplamı + onay sayıları; taşeron hakediş toplamı
const cardsFor = (view, t, c) => {
  if (view === "branch") return [
    { label: "Toplam Kayıt", value: t.entries, icon: LayoutDashboard },
    { label: "Hizmet Toplamı", value: fmtTL(t.customer), icon: Wallet },
    { label: "Onay Bekleyen", value: t.pending, icon: Clock },
    { label: "Onaylı", value: t.approved, icon: CheckCircle2 },
  ];
  if (view === "scoped") return [
    { label: "Toplam Kayıt", value: t.entries, icon: LayoutDashboard },
    { label: "Müşteri Toplamı", value: fmtTL(t.customer), icon: Wallet },
    { label: "Brüt Kâr", value: fmtTL(t.gross_profit), icon: ArrowUpRight },
    { label: "Onay Bekleyen", value: t.pending, icon: Clock },
  ];
  if (view === "subcontractor") return [
    { label: "Toplam İş", value: t.entries, icon: LayoutDashboard },
    { label: "Hakediş Toplamı", value: fmtTL(t.subcontractor), icon: Wallet },
    { label: "Onay Bekleyen", value: t.pending, icon: Clock },
    { label: "Onaylı", value: t.approved, icon: CheckCircle2 },
  ];
  const k = c?.totals || { asil: 0, gecici: 0, personnel: 0, branches: 0, atms: 0, projects: 0 };
  const tv = k.turnover || { year_total: 0, prev_year_total: 0, growth_pct: null };
  const g = tv.growth_pct;
  const growth = g == null
    ? { text: tv.year_total ? "geçen yıl veri yok" : "veri yok", tone: "text-slate-500", icon: Minus }
    : g > 0 ? { text: `+%${fmtNum(g, 1)} büyüme`, tone: "text-emerald-700", icon: ArrowUpRight }
    : g < 0 ? { text: `%${fmtNum(g, 1)} küçülme`, tone: "text-red-700", icon: ArrowDownRight }
    : { text: "%0 değişim yok", tone: "text-slate-500", icon: Minus };
  const year = c?.year ?? new Date().getFullYear();
  return [
    { label: "Toplam Çalışan", value: k.personnel, sub: `Asıl ${k.asil} · Geçici ${k.gecici}${k.yonetim ? ` · Yönetim ${k.yonetim}` : ""}`, icon: Users },
    { label: "Toplam ATM", value: k.atms, icon: Landmark },
    { label: "Toplam Şube", value: k.branches, icon: Building2 },
    { label: "Toplam Proje", value: k.projects, icon: FolderKanban },
    { label: `Toplam Ciro (${year})`, key: "Toplam Ciro", value: fmtTL(tv.year_total), icon: Wallet, growth, sub: `${year - 1}: ${fmtTL(tv.prev_year_total)}`, title: "Yalnız şube onayı tamamlanmış hizmet kayıtları" },
    { label: `Aktif Kayıt (${year})`, key: "Aktif Kayıt", value: t.entries, sub: `Onay bekleyen ${t.pending} · Onaylı ${t.approved}`, icon: LayoutDashboard, title: `${year} yılı aktif hizmet kayıtları (tüm projeler)` },
    { label: `Müşteri Toplamı (${year})`, key: "Müşteri Toplamı", value: fmtTL(t.customer), icon: Wallet, title: `${year} yılı aktif kayıtların müşteri tutarı` },
    { label: `Taşeron Toplamı (${year})`, key: "Taşeron Toplamı", value: fmtTL(t.subcontractor), icon: Wallet, title: `${year} yılı aktif kayıtların taşeron tutarı` },
    { label: `Brüt Kâr (${year})`, key: "Brüt Kâr", value: fmtTL(t.gross_profit), icon: ArrowUpRight, title: `Müşteri − Taşeron (${year} aktif kayıtları)` },
    { label: `Onay Bekleyen (${year})`, key: "Onay Bekleyen", value: t.pending, icon: Clock, title: `${year} yılında şube onayı bekleyen hizmet kayıtları` },
  ];
};

const COLS = {
  admin: [["Kayıt", "entries"], ["Onay Bekleyen", "pending"], ["Müşteri Toplamı", "customer"], ["Taşeron Toplamı", "subcontractor"], ["Brüt Kâr", "gross_profit"]],
  scoped: [["Kayıt", "entries"], ["Müşteri Toplamı", "customer"], ["Taşeron Toplamı", "subcontractor"], ["Brüt Kâr", "gross_profit"]],
  branch: [["Kayıt", "entries"], ["Hizmet Toplamı", "customer"]],
  subcontractor: [["İş", "entries"], ["Hakediş Toplamı", "subcontractor"]],
};
const cell = (key, v) => (key === "entries" || key === "pending" ? v : fmtTL(v));

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYearState] = useState(() => Number(localStorage.getItem("dash.year")) || null);
  const setYear = (y) => { setYearState(y); localStorage.setItem("dash.year", String(y)); };
  const [updatedAt, setUpdatedAt] = useState("");

  const load = useCallback(() => http.get("/dashboard", { params: year ? { year } : {} }).then((r) => { setData(r.data); setUpdatedAt(new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })); }).catch(() => setData((d) => d || EMPTY)), [year]);

  useEffect(() => { load(); }, [load]);

  // Proje kartı / veri girişi / onay değişiklikleri anında yansısın: pencere odağında + görünürken 30 sn'de bir tazele
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const t = setInterval(refresh, 30000);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); clearInterval(t); };
  }, [load]);

  if (!data) return <div className="text-slate-500 text-sm">Yükleniyor…</div>;
  const view = data.view || "admin";
  const cols = COLS[view];
  const scoped = view !== "admin";

  return (
    <div className="space-y-8" data-testid="dashboard" data-view={view}>
      <WelcomeCard year={year || data.cards?.year} years={data.cards?.years} onYear={setYear} />
      <InsuranceDueCard />
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Genel Bakış</h1>
        <p className="text-sm text-slate-500 mt-1">{scoped ? "Yalnız yetkili olduğunuz kapsamdaki aktif kayıtlar." : `Çalışan, şube, ATM ve proje sayıları; kayıt / tutar özetleri ${data.year || year || new Date().getFullYear()} yılına ait, ciro yalnız şube onayı tamamlanmış hizmetlerden. Yılı üstteki karttan değiştirin.`}</p>
        {scoped && data.scope_names?.length > 0 && (
          <div className="mt-2 inline-flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900" data-testid="dashboard-scope">
            <Building2 className="w-4 h-4 shrink-0 mt-px" />
            <span><span className="font-semibold">{view === "subcontractor" ? "Taşeron kapsamı" : view === "scoped" ? "Projeleriniz" : "Şubeleriniz"}:</span> {data.scope_names.join(", ")}</span>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${view === "admin" ? "lg:grid-cols-3 xl:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
        {cardsFor(view, data.totals, data.cards).map((c) => (
          <div key={c.key || c.label} className="kpi" data-testid={`kpi-${c.key || c.label}`} title={c.title}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{c.label}</div>
                <div className={`mt-2 ${c.growth ? "text-xl" : "text-2xl"} font-bold font-mono tracking-tight text-slate-900`}>{c.value}</div>
                {c.growth && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${c.growth.tone}`} data-testid="kpi-growth">
                    <c.growth.icon className="w-3.5 h-3.5" /> {c.growth.text}
                  </div>
                )}
                {c.sub && <div className="mt-1 text-xs text-slate-500 font-mono" data-testid={`kpi-sub-${c.key || c.label}`}>{c.sub}</div>}
              </div>
              <c.icon className="w-5 h-5 text-amber-600 shrink-0" />
            </div>
          </div>
        ))}
      </div>

      {view !== "subcontractor" && <PendingApprovalsPanel totals={data.totals} />}

      {view === "admin" && <ProjectCards cards={data.cards} year={year} onYear={setYear} updatedAt={updatedAt} onRefresh={load} />}

      {isAdmin && <InstallmentCalendar canPay={isAdmin} />}

      <BranchSummary locations={data.locations} view={view} />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">Proje Bazlı Özet{!scoped && data.year ? ` (${data.year})` : ""}</div>
          <div className="text-xs text-slate-500">{scoped ? "Aktif kayıtlar üzerinden hesaplanmıştır." : `${data.year} yılı aktif kayıtları üzerinden hesaplanmıştır.`}</div>
        </div>
        {data.projects.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            {scoped ? "Kapsamınızda henüz kayıt yok." : <>Henüz kayıt yok. Sol menüden <span className="font-semibold">Demo verisini yükle</span> ile başlayın.</>}
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="dashboard-projects-scroll">
          <table className="w-full tbl min-w-[640px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Proje</th>
                {cols.map(([h]) => <th key={h} className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.projects.map((p) => (
                <tr key={p.project_id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{p.project_name}</td>
                  {cols.map(([h, key]) => <td key={h} className={`numeric-cell px-4 py-2.5 text-sm ${key === "gross_profit" ? "font-semibold text-emerald-700" : ""}`}>{cell(key, p[key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
